import React, { useContext, useState, useEffect, useMemo } from "react";
import "../../../App.css";
import { useLocation, useNavigate } from "react-router-dom";
import { alpha, ThemeProvider } from "@mui/material/styles";
import {
  format,
  differenceInSeconds,
  addSeconds,
  intervalToDuration,
} from "date-fns";
import { greenlightIcon } from "../../../assets/imgs";
import RecordTable, { TableHeader } from "../../../components/RecordTable";
import type { } from "@mui/x-data-grid/themeAugmentation";
import styled from "@emotion/styled";
import ScannerInput from "../../../components/ScannerInput";
import Signalr from "../../../features/SignalR/SignalR";
import {
  useCountInMutation,
  useCountOutMutation,
  useLazyGetScheduleByScheduleIdQuery,
  useScheduleCheckOutMutation,
  useWorkerCheckInMutation,
  useWorkerCheckOutMutation,
} from "../../../features/schedule/scheduleSlice";
import dayjs from "dayjs";
import {
  useStartMonitoringMutation,
  useStopMonitoringMutation,
} from "../../../features/sensors/sensorsSlice";
import HeartRateHub from "../../../features/SignalR/HeartRate";
import useInOutHub from "../../../features/SignalR/InOutHub";
import BloodOxygenHub from "../../../features/SignalR/BloodOxygen";
import { useTranslation } from "react-i18next";
import WatchGPSHub from "../../../features/SignalR/WatchGPS";
import { SendDataToWorkScheduleHub } from "../../../features/SignalR/WorkScheduleHub";
import {
  Actions,
  EntryContext,
  useSubState,
  WorkerStatus,
} from "../shared/State";
import {
  buttonStyle,
  CollapsableDiv,
  defaultTheme,
  GreyButtonStyle,
  ModalTrigger,
  TimedNode,
} from "../shared/SharedComponents";
import {
  selectCurrentUser,
  selectPermissions,
} from "../../../features/auth/authSlice";
import { Permissions } from "../../../utils/Permissions";
import ZetronHub from "../../../features/SignalR/ZetronHub";
import BodyTemperatureHub from "../../../features/SignalR/BodyTemperature";

export const RealTimeMonitoringWorkDiv = ({ entryId }: { entryId: number }) => {
  const [entryContext, realTimeStatus] = useSubState(
    EntryContext,
    (x) => x.realTimeStatus!
  );
  console.log(
    "RealTimeMonitoringWorkDiv - Current worker statuses:",
    realTimeStatus?.workerStatuses
  );

  const { t, i18n } = useTranslation();

  const getLocation = useLocation();
  const schedule = getLocation.state?.schedule;
  const workType = schedule.workType;
  const [_, isAllowedCheckIn] = useSubState(
    EntryContext,
    (x) => x.isAllowedCheckIn
  );

  const { start } = useInOutHub({
    setInOutData: (inCount: number, outCount: number) => {
      entryContext!.update(Actions.updateInAICount(inCount));
      entryContext!.update(Actions.updateOutAICount(outCount));
    },
  });

  const [getScheduleQuery] = useLazyGetScheduleByScheduleIdQuery();
  const [startMonitoring] = useStartMonitoringMutation();

  useEffect(() => {
    if (schedule?.id != undefined) start(schedule.id);

    getScheduleQuery({
      id: schedule?.id,
    }).then((respone: any) => {
      if (workType === "Confined Space") {
        SendDataToWorkScheduleHub({
          data: "in: " + respone?.data?.countIn?.toString(),
        });
        SendDataToWorkScheduleHub({
          data: "out: " + respone?.data?.countOut?.toString(),
        });
        SendDataToWorkScheduleHub({
          data: "inAI: " + respone?.data?.countInByAI?.toString(),
        });
        SendDataToWorkScheduleHub({
          data: "outAI: " + respone?.data?.countOutByAI?.toString(),
        });
      }

      entryContext!.update(Actions.updateInCount(respone?.data?.countIn));
      entryContext!.update(Actions.updateOutCount(respone?.data?.countOut));
      entryContext!.update(Actions.updateInAICount(respone?.data?.countInByAI));
      entryContext!.update(
        Actions.updateOutAICount(respone?.data?.countOutByAI)
      );
      entryContext!.update(
        Actions.updateStandByCount(
          respone?.data?.workers.filter((x: any) => x.typeId == 2).length
        )
      );
      entryContext!.update(
        Actions.updateTotalount(respone?.data?.workers.length)
      );
    });
  }, [schedule]);

  useEffect(() => {
    console.log(
      "realTimeStatus?.workerStatuses:",
      realTimeStatus?.workerStatuses
    );
    var hubFinishers: (() => void)[] = []
    const blackLineEquipmentIds = realTimeStatus?.workerStatuses
      .filter((x) => x.type == "3")
      .map((y, i) =>
        y.equipments
          ?.filter((x: any) => x.realTimeResourceTypeId === 1)
          ?.map((x: any) => x.serialNumber)
      );

    if (blackLineEquipmentIds) {
      blackLineEquipmentIds.forEach((equipmentIds: any) => {
        if (equipmentIds) {
          equipmentIds.forEach((equipmentId: any) => {
            if (equipmentId) {
              hubFinishers.push(
                Signalr({
                  sn: equipmentId,
                  setSensorDataArray: (data: any) => {
                    entryContext!.update(Actions.updateGasSensor(data));
                  },
                })
              );
            }
          });
        }
      });
    }

    const heartRateEquipmentIds = realTimeStatus?.workerStatuses
      .filter((x) => x.type == "3")
      .map((y, i) =>
        y.equipments
          ?.filter((x: any) => x.realTimeResourceTypeId === (workType === "Confined Space" ? 2 : 6))
          ?.map((x: any) => x.serialNumber)
      );

    console.log("heartRateEquipmentIds:", heartRateEquipmentIds);
    if (heartRateEquipmentIds) {
      heartRateEquipmentIds.forEach((equipmentIds: any) => {
        if (equipmentIds) {
          equipmentIds.forEach((equipmentId: any) => {
            if (equipmentId) {
              hubFinishers.push(
                HeartRateHub({
                  sn: equipmentId,
                  setHeartRateData: (data: any) => {
                    entryContext!.update(Actions.updateHeartRateSensor(data));
                    console.log("setHeartRateData data in console:", data);
                  },
                })
              );

              hubFinishers.push(
                BloodOxygenHub({
                  sn: equipmentId,
                  setBloodOxygenData: (data: any) => {
                    entryContext!.update(Actions.updateBloodOxygenSensor(data));
                  },
                })
              );
              
              hubFinishers.push(
                  WatchGPSHub({
                  sn: equipmentId,
                  setGPSData: (data: any) => {
                    entryContext!.update(Actions.updateGPSSensor(data));
                  },
                })
              );

              hubFinishers.push(
                BodyTemperatureHub({
                  sn: equipmentId,
                  setBodyTemperatureData: (data: any) => {
                    entryContext!.update(
                      Actions.updateBodyTemperatureSensor(data)
                    );
                    console.log("BodyTemperatureHub data in console:", data);
                  },
                })
              );
            }
          });
        }
      });
    }

    return () => {
      hubFinishers.forEach(x => x())
    }
  }, [JSON.stringify(realTimeStatus?.workerStatuses)]);

  return (
    <>
      <CollapsableDiv title={t("Real Time Monitoring Worker")}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            fontSize: "14px",
            rowGap: "10px",
          }}
        >
          <RealTimeMemberCountDiv>
            {/* <EntryInfo label='Total' value={realTimeStatus.total.toString()} /> */}
            <EntryInfo
              label={t("Total people")}
              value={realTimeStatus.total.toString()}
            />
            <EntryInfo label={t("In")} value={realTimeStatus.in.toString()} />
            <EntryInfo label={t("Out")} value={realTimeStatus.out.toString()} />
          </RealTimeMemberCountDiv>
          <RealTimeMemberCountDiv>
            <EntryInfo
              label={t("Stand by")}
              value={realTimeStatus.standBy.toString()}
            />
            {/* <EntryInfo label={t('AI In')} value={realTimeStatus.inAI.toString()} />
                        <EntryInfo label={t('AI Out')} value={realTimeStatus.outAI.toString()} /> */}
          </RealTimeMemberCountDiv>

          {realTimeStatus?.workerStatuses
            .filter((x) => x.type == "3")
            .map((y, i) => (
              <WorkerInfo key={i} workerStatus={y} index={i} workType={workType} />
            ))}
          {/* <CheckOutProjectDiv /> */}
        </div>
      </CollapsableDiv>
      <CollapsableDiv title={t("Other People")}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            fontSize: "14px",
            rowGap: "10px",
          }}
        >
          {realTimeStatus?.workerStatuses
            .filter((x) => x.type != "3")
            .map((x, i) => (
              <WorkerInfo key={i} workerStatus={x} index={i} workType={workType} />
            ))}
          {isAllowedCheckIn && <CheckOutProjectDiv />}
        </div>
      </CollapsableDiv>
    </>
  );
};

const RealTimeMemberCountDiv = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 10px;
  }
`;

const EntryInfo = ({ label, value }: { label: string; value: string }) => (
  <div style={{ display: "flex", alignItems: "baseLine" }}>
    {label}:{" "}
    <div
      style={{
        marginLeft: "5px",
        fontSize: "18px",
        borderRadius: "7.5px",
        textAlign: "center",
        width: "50px",
        backgroundColor: "white",
        color: "black",
      }}
    >
      {value}
    </div>
  </div>
);

const WorkerInfo = ({
  workerStatus,
  index,
  workType,
}: {
  workerStatus: WorkerStatus;
  index: number;
  workType: string;
}) => {
  console.log("WorkerInfo rendering for worker:", workerStatus.id);
  console.log("Worker body temperature:", workerStatus.bodyTemperature);
  console.log("Worker equipment IDs:", workerStatus.equipmentIds);
  console.log(
    "Worker realTimeResourceTypeId:",
    workerStatus.realTimeResourceTypeId
  );

  const [_, isAllowedCheckIn] = useSubState(
    EntryContext,
    (x) => x.isAllowedCheckIn
  );

  const { t, i18n } = useTranslation();
  const singleFlexStyle: React.CSSProperties = { flex: "1 1 0px" };
  const alignChildrenCenterStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };
  const timeDivStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    borderRadius: "7.5px",
    height: "30px",
    width: "85px",
    backgroundColor: "white",
    color: "black",
  };
  const workerInfoDivStyle: React.CSSProperties = {
    backgroundColor: alpha("#4A7BD7", 0.3),
    borderRadius: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
  };
  var addTimeZone = (date: Date | string | undefined) =>
    date == undefined || (date as any) == "" || date == null || !date
      ? undefined
      : dayjs(date, "DD/MM/YYYY HH:mm:ss").toDate();

  const curTimeIn = addTimeZone(workerStatus.noOfEntries[0]?.timeIn);
  const curTimeOut = addTimeZone(workerStatus.noOfEntries[0]?.timeOut);

  const getTimeString = (fromDate: Date, toDate: Date) => {
    var duration = intervalToDuration({
      start: 0,
      end: differenceInSeconds(toDate, fromDate) * 1000,
    });
    duration.hours = (duration.hours ?? 0) + (duration.days ?? 0) * 24;
    const zeroPad = (num: number | undefined) =>
      String(num ?? 0).padStart(2, "0");
    return `${zeroPad(duration.hours)}:${zeroPad(duration.minutes)}:${zeroPad(
      duration.seconds
    )}`;
  };
  const isValidTime = (date: Date | undefined) => {
    return date && !isNaN(date.getTime());
  };

  const totalTime = useMemo(() => {
    if (isValidTime(curTimeIn) && isValidTime(curTimeOut)) {
      return getTimeString(curTimeIn!, curTimeOut!);
    }
    return undefined;
  }, [curTimeIn, curTimeOut]);

  const isWorkerIn =
    workerStatus.noOfEntries[workerStatus.noOfEntries.length - 1]?.timeIn;
  const isWorkerOut =
    workerStatus.noOfEntries[workerStatus.noOfEntries.length - 1]?.timeOut;

  const [entryContext, realTimeStatus] = useSubState(
    EntryContext,
    (x) => x.realTimeStatus!
  );

  var type = "";
  if (workerStatus.type == "1") {
    type = t("Competent person");
  } else if (workerStatus.type == "2") {
    type = t("Standby Person");
  } else type = t("Certified Worker ") + `${index + 1}`;

  useEffect(() => {
    const blackLineEquipmentIds = workerStatus?.equipments
      ?.filter((x: any) => x.realTimeResourceTypeId === 1)
      ?.map((x: any) => x.serialNumber);

    if (blackLineEquipmentIds) {
      blackLineEquipmentIds.forEach((equipmentId: any) => {
        if (equipmentId) {
          Signalr({
            sn: equipmentId,
            setSensorDataArray: (data: any) => {
              entryContext!.update(Actions.updateGasSensor(data));
            },
          });
        }
      });
    }

    const heartRateEquipmentIds = workerStatus?.equipments
      ?.filter((x: any) => x.realTimeResourceTypeId === 6)
      ?.map((x: any) => x.serialNumber); //id

    if (heartRateEquipmentIds) {
      heartRateEquipmentIds.forEach((equipmentId: any) => {
        if (equipmentId) {
          HeartRateHub({
            sn: equipmentId,
            setHeartRateData: (data: any) => {
              entryContext!.update(Actions.updateHeartRateSensor(data));
            },
          });
          BloodOxygenHub({
            sn: equipmentId,
            setBloodOxygenData: (data: any) => {
              entryContext!.update(Actions.updateBloodOxygenSensor(data));
            },
          });
          WatchGPSHub({
            sn: equipmentId,
            setGPSData: (data: any) => {
              entryContext!.update(Actions.updateGPSSensor(data));
            },
          });
          BodyTemperatureHub({
            sn: equipmentId,
            setBodyTemperatureData: (data: any) => {
              entryContext!.update(Actions.updateBodyTemperatureSensor(data));
            },
          });
        }
      });
    }
  }, [JSON.stringify(workerStatus?.equipments)]);

  return (
    <div
      style={{
        backgroundColor: alpha("#052A70", 0.8),
        borderRadius: "10px",
        padding: "10px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div
          style={{
            ...singleFlexStyle,
            display: "flex",
            flexWrap: "wrap",
            alignContent: "start",
          }}
        >
          <div>{type}</div>
        </div>
        <div
          style={{
            ...singleFlexStyle,
            zIndex: "1",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              marginTop: `${workerStatus.realTimeResourceTypeId?.includes(1)
                ? `120px`
                : `60px`
                }`,
              marginBottom: "10px",
              fontSize: "16px",
            }}
          >
            {workerStatus.name}
          </div>
        </div>
        <div
          style={{
            ...singleFlexStyle,
            display: "flex",
            flexDirection: "column",
            fontSize: "18px",
            gap: "10px",
            alignItems: "end",
          }}
        >
          {
            <>
              {isAllowedCheckIn && (
                <>
                  <ModalTrigger
                    renderTrigger={(open, setOpen) => (
                      <button
                        style={{ ...buttonStyle, fontFamily: "Russo One" }}
                        onClick={() => setOpen(true)}
                      >
                        IN
                      </button>
                    )}
                    renderContent={(open, setOpen) => (
                      <CheckEquipmentTable
                        workerId={workerStatus.id}
                        workerStatus={workerStatus}
                        open={open}
                        action={"in"}
                        setOpen={setOpen}
                      />
                    )}
                  />
                  <ModalTrigger
                    renderTrigger={(open, setOpen) => (
                      <button
                        style={{ ...buttonStyle, fontFamily: "Russo One" }}
                        onClick={() => setOpen(true)}
                      >
                        OUT
                      </button>
                    )}
                    renderContent={(open, setOpen) => (
                      <CheckEquipmentTable
                        workerId={workerStatus.id}
                        workerStatus={workerStatus}
                        open={open}
                        action={"out"}
                        setOpen={setOpen}
                      />
                    )}
                  />
                </>
              )}
              {workerStatus.realTimeResourceTypeId?.includes(1) && (
                <button
                  style={
                    workerStatus.showGasGPS
                      ? { ...buttonStyle, fontFamily: "Russo One" }
                      : { ...GreyButtonStyle, fontFamily: "Russo One" }
                  }
                  onClick={() => {
                    workerStatus.showGasGPS = !workerStatus.showGasGPS;
                    entryContext?.update(
                      Actions.updateGasGpsDisplay(
                        workerStatus.showGasGPS,
                        workerStatus.id
                      )
                    );
                  }}
                >
                  Gas GPS
                </button>
              )}
              {workerStatus.realTimeResourceTypeId?.includes(2) && (
                <button
                  style={
                    workerStatus.showWatchGPS
                      ? { ...buttonStyle, fontFamily: "Russo One" }
                      : { ...GreyButtonStyle, fontFamily: "Russo One" }
                  }
                  onClick={() => {
                    workerStatus.showWatchGPS = !workerStatus.showWatchGPS;
                    entryContext?.update(
                      Actions.updateWatchGpsDisplay(
                        workerStatus.showWatchGPS,
                        workerStatus.id
                      )
                    );
                  }}
                >
                  Mobile GPS
                </button>
              )}
            </>
          }
        </div>
      </div>
      <div
        style={{
          alignSelf: "center",
          marginTop: "5px",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "220px",
            marginLeft: "-110px",
            height: "100px",
            marginTop: "-80px",
            background:
              isWorkerIn && isWorkerOut
                ? "radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 0, 0.8) 0%, rgba(255, 255, 0, 0) 100%)"
                : isWorkerIn
                  ? "radial-gradient(50% 50% at 50% 50%, rgba(255, 0, 0, 0.8) 0%, rgba(255, 0, 0, 0) 100%)"
                  : "radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 0, 0.8) 0%, rgba(255, 255, 0, 0) 100%)",
          }}
        ></div>
      </div>
      <div
        style={{
          display: "flex",
          zIndex: 1,
          marginTop: "30px",
          marginBottom: "10px",
        }}
      >
        <div style={{ ...singleFlexStyle, ...alignChildrenCenterStyle }}>
          <div>{t("Time In")}</div>
          <div style={timeDivStyle}>
            {curTimeIn && isValidTime(curTimeIn)
              ? format(curTimeIn, "HH:mm:ss")
              : "--:--:--"}
          </div>
        </div>
        <div style={{ ...singleFlexStyle, ...alignChildrenCenterStyle }}>
          <div>{t("Time Out")}</div>
          <div style={timeDivStyle}>
            {curTimeOut && isValidTime(curTimeOut)
              ? format(curTimeOut, "HH:mm:ss")
              : "--:--:--"}
          </div>
        </div>
        <div style={{ ...singleFlexStyle, ...alignChildrenCenterStyle }}>
          <div>{t("Timer")}</div>
          {!curTimeIn ? (
            <div style={timeDivStyle}>--:--:--</div>
          ) : curTimeIn < new Date() && !totalTime ? (
            <TimedNode
              calculateInterval={(time) => 1000 - time.getMilliseconds()}
              render={(time) => {
                return (
                  <div style={timeDivStyle}>
                    {getTimeString(curTimeIn!, time)}
                  </div>
                );
              }}
            />
          ) : (
            <div style={timeDivStyle}>{totalTime}</div>
          )}
        </div>
      </div>
      {workerStatus.type != "1" && (
        <CollapsableDiv notExpanded={workerStatus.type == "1"}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
              fontSize: "16px",
              alignItems: "baseline",
              marginTop: "15px",
            }}
          >
            <WorkerInfoSmallDiv
              style={{ ...workerInfoDivStyle, padding: "5px" }}
            >
              <div style={{
                fontWeight: "500", flex: "20 0 0px",
                gridRow: workType === "Surface" ? "1 / 2" : "1 / 3",
              }}>
                {t("Heart Rate")}
              </div>
              <div
                style={{
                  flex: "3 0 0px",
                  display: "grid",
                  gridTemplateColumns: "min-content 2fr",
                }}
              >
                <div
                  style={{
                    justifySelf: "end",
                    paddingRight: "5px",
                    color: "#3EDF24",
                  }}
                >
                  {workerStatus.heartRate}
                </div>
                <div>bpm</div>
                {/* &nbsp; */}
              </div>
            </WorkerInfoSmallDiv>
            {workType === "Surface" && (
              <WorkerInfoSmallDiv
                style={{ ...workerInfoDivStyle, padding: "5px" }}
              >
                <div style={{ fontWeight: "500", flex: "20 0 0px" }}>
                  {t("Body Temperature")}
                </div>
                <div
                  style={{
                    justifySelf: "end",
                    paddingRight: "5px",
                    color: "#3EDF24",
                  }}
                >
                  {workerStatus.bodyTemperature !== undefined && workerStatus.bodyTemperature !== null
                    ? Number(workerStatus.bodyTemperature).toFixed(1)
                    : ""}
                </div>
                <div
                  style={{
                    flex: "3 0 0px",
                    display: "grid",
                    gridTemplateColumns: "min-content 2fr",
                  }}
                >
                  <div style={{ gridColumn: "2 / 3", justifySelf: "start" }}>
                    °C
                  </div>
                </div>
              </WorkerInfoSmallDiv>
            )}


            {workType === "Surface" 
              && workerStatus.lastBloodPressureHigher !== undefined 
              && workerStatus.lastBloodPressureHigher !== null
              &&
              (
              <WorkerInfoSmallDiv
                style={{ ...workerInfoDivStyle, padding: "5px", gridColumn: "span 2" }}
              >
                <div style={{ fontWeight: "500", flex: "20 0 0px" }}>
                  {t("Blood Pressure")}
                </div>
                <div
                  style={{
                    justifySelf: "end",
                    paddingRight: "5px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "5px",
                  }}
                >
                  <div>Higher</div>
                  <div style={{ color: "#3EDF24", justifySelf: "end" }}>
                    {workerStatus.lastBloodPressureHigher !== undefined && workerStatus.lastBloodPressureHigher !== null
                      ? Number(workerStatus.lastBloodPressureHigher)
                      : ""}
                  </div>
                  <div>Lower</div>
                  <div style={{ color: "#3EDF24", justifySelf: "end" }}>
                    {workerStatus.lastBloodPressureLower !== undefined && workerStatus.lastBloodPressureLower !== null
                      ? Number(workerStatus.lastBloodPressureLower)
                      : ""}
                  </div>
                </div>
              </WorkerInfoSmallDiv>
            )}

            {workType === "Confined Space" && (
              <WorkerInfoSmallDiv
                style={{ ...workerInfoDivStyle, padding: "5px", }}
              >
                <>
                  <div style={{ fontWeight: "500", flex: "20 0 0px" }}>
                    {t("Gas Sensor Status")}
                  </div>
                  <div
                    style={{
                      flex: "5 0 0px",
                      display: "grid",
                      gridTemplateColumns: "min-content 2fr",
                    }}
                  >
                    <div style={{ gridColumn: "2 / 3", justifySelf: "start" }}>
                      {t("Offline")}
                    </div>
                    <img
                      height="25"
                      style={{
                        gridColumn: "1 / 2",
                        gridRow:
                          (workerStatus?.isOnline ?? false) == false
                            ? "1 / 2"
                            : "2 / 3",
                        filter:
                          (workerStatus?.isOnline ?? false) == false
                            ? "hue-rotate(315deg) brightness(1.1)"
                            : "",
                      }}
                      src={greenlightIcon}
                    />
                    <div style={{ gridColumn: "2 / 3", justifySelf: "start" }}>
                      {t("Online")}
                    </div>
                  </div>
                </>

              </WorkerInfoSmallDiv>
            )}
            {workType === "Confined Space" &&
              <div
                style={{
                  ...workerInfoDivStyle,
                  gridColumn: "span 2",
                  display: "grid",
                  gridTemplateColumns: "5fr 3fr 4fr 6fr 3fr 3fr",
                  justifyContent: "end",
                  padding: "5px",
                }}
              >
                <div style={{ paddingLeft: "0px" }}>{t("CO2")}:</div>
                <div
                  style={{
                    justifySelf: "end",
                    paddingRight: "5px",
                    color: "#3EDF24",
                  }}
                >
                  {workerStatus.co2}
                </div>
                <div>ppm</div>
                <div style={{ paddingLeft: "0px" }}>{t("LEL")}:</div>
                <div
                  style={{
                    justifySelf: "end",
                    paddingRight: "5px",
                    color: "#3EDF24",
                  }}
                >
                  {workerStatus.ch4}
                </div>
                <div>%</div>
                <div style={{ paddingLeft: "0px" }}>{t("O2")}:</div>
                <div
                  style={{
                    justifySelf: "end",
                    paddingRight: "5px",
                    color: "#3EDF24",
                  }}
                >
                  {workerStatus.o2}
                </div>
                <div>%</div>
                <div style={{ paddingLeft: "0px" }}>{t("H2S")}:</div>
                <div
                  style={{
                    justifySelf: "end",
                    paddingRight: "5px",
                    color: "#3EDF24",
                  }}
                >
                  {workerStatus.h2s}
                </div>
                <div>ppm</div>
                <div style={{ paddingLeft: "0px" }}>{t("CO")}:</div>
                <div
                  style={{
                    justifySelf: "end",
                    paddingRight: "5px",
                    color: "#3EDF24",
                  }}
                >
                  {workerStatus.co}
                </div>
                <div>ppm</div>
              </div>
            }
          </div>
        </CollapsableDiv>
      )}
    </div>
  );
};

const WorkerInfoSmallDiv = styled.div`
  display: flex;
  @media (max-width: 429.9px) {
    display: block;
  }
`;

const CheckOutProjectDiv = ({ }: {}) => {
  const { t, i18n } = useTranslation();
  const [scheduleCheckOut] = useScheduleCheckOutMutation();
  const navigate = useNavigate();
  const getLocation = useLocation();
  const schedule = getLocation.state?.schedule;
  return (
    <div
      style={{
        backgroundColor: alpha("#052A70", 0.8),
        borderRadius: "10px",
        padding: "10px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <ModalTrigger
        renderTrigger={(open, setOpen) => (
          <button
            onClick={() => setOpen(true)}
            style={{
              background: "linear-gradient(180deg, #E2FFA5 0%, #43B656 100%)",
              fontFamily: "Russo One",
              fontSize: "20px",
              width: "fit-content",
              color: "white",
              borderRadius: "10px",
              paddingLeft: "15px",
              paddingRight: "15px",
              paddingTop: "10px",
              paddingBottom: "10px",
              border: "none",
              cursor: "pointer",
            }}
          >
            {t("Check Out Project")}
          </button>
        )}
        renderContent={(open, setOpen) => (
          <div
            style={{
              minWidth: "400px",
              maxWidth: "35vw",
              borderRadius: "15px",
              backgroundColor: "#E9F5FF",
              fontSize: "18px",
              fontFamily: "Faune",
              padding: "30px",
            }}
          >
            <div
              style={{
                color: "black",
                borderRadius: "15px",
                backgroundColor: "white",
                padding: "10px",
              }}
            >
              Are you sure to checkout this project?
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                columnGap: "50px",
                marginTop: "10px",
              }}
            >
              <button
                style={{ ...buttonStyle, fontFamily: "Faune" }}
                onClick={() => {
                  setOpen(false);
                  scheduleCheckOut({
                    workScheduleId: schedule?.id.toString(),
                  }).then(() => {
                    alert("Check out successfully");
                    navigate("/dashboard");
                  });
                }}
              >
                Yes
              </button>
              <button
                style={{ ...buttonStyle, fontFamily: "Faune" }}
                onClick={() => setOpen(false)}
              >
                No
              </button>
            </div>
          </div>
        )}
      />
    </div>
  );
};

const BorderedTh = styled.th`
  border: 1px black solid;
  border-collapse: collapse;
  text-align: left;
  width: max-content;
  white-space: nowrap;
  padding: 0px 10px;
  font-weight: 400;
`;

const BorderedTd = styled.td`
  border: 1px black solid;
  border-collapse: collapse;
  padding: 0px 10px;
`;

const NoOfEntriesTable = ({
  workerId,
  open,
  setOpen,
}: {
  workerId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const [entryContext, noOfEntries] = useSubState(
    EntryContext,
    (x) =>
      x.realTimeStatus.workerStatuses.find((x) => x.id == workerId)?.noOfEntries
  );

  return (
    <div
      style={{
        minWidth: "400px",
        maxWidth: "80vw",
        borderRadius: "15px",
        backgroundColor: "#E9F5FF",
        fontSize: "18px",
        fontFamily: "Poppins",
        padding: "30px",
      }}
    >
      <div
        style={{
          color: "black",
          borderRadius: "15px",
          backgroundColor: "white",
          padding: "10px",
        }}
      >
        <div style={{ textDecoration: "underline", fontSize: "20px" }}>
          No. of Entries
        </div>
        <div style={{ maxWidth: "100%", overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: "18px" }}>
            <thead>
              <tr>
                <BorderedTh>No. of Entries</BorderedTh>
                <BorderedTh>Time In</BorderedTh>
                <BorderedTh>Time Out</BorderedTh>
                <BorderedTh>Total Time</BorderedTh>
              </tr>
            </thead>
            {noOfEntries && noOfEntries[noOfEntries.length - 1]?.timeIn && (
              <tbody>
                {noOfEntries
                  ?.slice()
                  .reverse()
                  .map((x, i) => (
                    <tr key={i}>
                      <BorderedTd>{x.number}</BorderedTd>
                      <BorderedTd>
                        {x.timeIn
                          ? dayjs(x.timeIn).format("dd/mm/yyyy HH:mm:ss")
                          : " "}
                      </BorderedTd>
                      <BorderedTd>
                        {x.timeOut
                          ? dayjs(x.timeOut).format("DD/MM/YYYY HH:mm:ss")
                          : " "}
                      </BorderedTd>
                      <BorderedTd>
                        {x.timeIn && x.timeOut
                          ? dayjs(
                            addSeconds(
                              new Date("0001-01-01T00:00:00"),
                              differenceInSeconds(x.timeOut!, x.timeIn!)
                            ),
                            "HH:mm:ss"
                          ).toString()
                          : ""}
                      </BorderedTd>
                    </tr>
                  ))}
              </tbody>
            )}
          </table>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          columnGap: "50px",
          marginTop: "10px",
        }}
      >
        <button
          style={{ ...buttonStyle, fontFamily: "Poppins" }}
          onClick={() => {
            setOpen(false);
          }}
        >
          Back
        </button>
      </div>
    </div>
  );
};

const CheckEquipmentTable = ({
  workerId,
  open,
  action,
  workerStatus,
  setOpen,
}: {
  workerId: string;
  open: boolean;
  action: string;
  workerStatus?: any;
  setOpen: (open: boolean) => void;
}) => {
  const { t, i18n } = useTranslation();
  const columns: TableHeader[] = [
    {
      field: "equipmentTypeName",
      headerName: t("Type"),
      width: 200,
      isSortable: true,
    },
    {
      field: "serialNumber",
      headerName: t("Serial Number"),
      width: 200,
      isSortable: false,
    },
    {
      field: "rfidNumber",
      headerName: t("RFID No."),
      width: 200,
      isSortable: false,
    },
    {
      field: "equipmentNumber",
      headerName: t("Equipment No."),
      width: 200,
      isSortable: false,
    },
    {
      field: "equipmentName",
      headerName: t("Equipment Name"),
      width: 200,
      isSortable: false,
    },
    {
      field: "manufacturer",
      headerName: t("Brand / Manufacturer"),
      width: 300,
      isSortable: true,
    },
    {
      field: "modelNumber",
      headerName: t("Model No."),
      width: 200,
      isSortable: false,
    },
    {
      field: "purchaseDate",
      headerName: t("Purchase Date"),
      width: 300,
      isSortable: false,
    },
    {
      field: "expiryDate",
      headerName: t("Expiry Date"),
      width: 300,
      isSortable: false,
    },
    {
      field: "certDueDate",
      headerName: t("Next Cal. / Cert Due Date"),
      width: 200,
      isSortable: false,
    },
  ];

  const rows = workerStatus?.equipments;

  const localButtonStyle = { fontFamily: "Faune", ...buttonStyle };
  const { boxShadow, backgroundColor, ...others } = localButtonStyle;

  const { scanValue } = ScannerInput();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [workerCheckIn] = useWorkerCheckInMutation();
  const [workerCheckOut] = useWorkerCheckOutMutation();
  const [startMonitoring] = useStartMonitoringMutation();
  const [stopMonitoring] = useStopMonitoringMutation();
  const [countIn] = useCountInMutation();
  const [countOut] = useCountOutMutation();
  const getLocation = useLocation();
  const entry = getLocation.state?.entry;
  const schedule = getLocation.state?.schedule;

  useEffect(() => {
    if (selectedRows.find((x) => x.rfid === scanValue)) return;
    rows.forEach((x: any) => {
      if (x.rfid === scanValue) selectedRows.push(x);
    });
    setSelectedRows(Array.from(selectedRows));
  }, [scanValue]);

  const isAllEquipmentReady = () => {
    var ready = true;
    rows.forEach((x: any) => {
      if (!selectedRows.find((x2) => x.id === x2.id)) {
        ready = false;
        return false;
      }
    });
    return ready;
  };

  const entryContext = useContext(EntryContext);

  return (
    <ThemeProvider theme={defaultTheme}>
      <div
        style={{
          minWidth: "400px",
          maxWidth: "90vw",
          padding: "15px",
          borderRadius: "15px",
          backgroundColor: "#E9F5FF",
          fontSize: "18px",
        }}
      >
        <div style={{ color: "black", fontSize: "22px", fontWeight: "500" }}>
          Check Equipment
        </div>
        <div
          style={{
            margin: workerStatus?.equipments.length > 0 ? "-28px" : "10px",
          }}
        >
          <RecordTable
            selectedRows={selectedRows}
            columns={columns}
            rows={rows}
            checkbox={true}
            deleteButton={false}
            onClick={(row, _) => {
              selectedRows.forEach((x, i) => {
                if (x.id === row.id) selectedRows.splice(i, 1);
              });
              setSelectedRows(Array.from(selectedRows));
            }}
          />
        </div>
        <div style={{ display: "flex", columnGap: "15px", marginTop: "10px" }}>
          <button
            style={localButtonStyle}
            onClick={() => {
              if (isAllEquipmentReady())
                entryContext?.update(Actions.confirmOut(workerId));
              setOpen(false);
              {
                action == "in"
                  ? workerCheckIn({
                    workScheduleId: schedule?.id.toString(),
                    workerId: workerId.toString(),
                    time: dayjs().format("DD/MM/YYYY HH:mm:ss").toString(),
                  }).then((res) => {
                    if ("error" in res) {
                      console.log(`Check in failed: `, res.error);
                      alert(`Check in failed: ${res.error}`);
                    } else {
                      alert("Check in Success");
                      countIn({ workScheduleId: schedule.id });
                      startMonitoring(
                        workerStatus?.equipments?.find(
                          (x: any) => x.realTimeResourceTypeId == 1
                        )?.id
                      ).then(() => window.location.reload());
                    }
                  })
                  : workerCheckOut({
                    workScheduleId: schedule?.id.toString(),
                    workerId: workerId.toString(),
                    time: dayjs().format("DD/MM/YYYY HH:mm:ss").toString(),
                  }).then((res) => {
                    if ("error" in res) {
                      console.log(`Check Out failed: `, res.error);
                      alert(`Check Out failed: ${res.error}`);
                    } else {
                      alert("Check Out Success");
                      countOut({ workScheduleId: schedule.id });
                      stopMonitoring(
                        workerStatus?.equipments?.find(
                          (x: any) => x.realTimeResourceTypeId == 1
                        )?.id
                      ).then(() => window.location.reload());
                    }
                  });
              }
            }}
          >
            Approve
          </button>
          <button
            style={{
              background:
                "linear-gradient(0deg,#EC8686,#EC8686), linear-gradient(0deg,#FF7A7A,#FF7A7A)",
              border: "1px solid #FF7A7A",
              boxShadow:
                "10px 4px 6px 0px #ED4C5C inset, 0px 4px 4px 0px #00000040",
              fontWeight: "700",
              ...others,
            }}
            onClick={() => setOpen(false)}
          >
            Reject
          </button>
        </div>
      </div>
    </ThemeProvider>
  );
};
